import {
  Box,
  Chip,
  Link as MuiLink,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { eq, type InitialQueryBuilder } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import { useState } from "react";

import { store } from "@/store/store";
import type { Item, Transcription } from "@/store/types";

type LanguageGroup = {
  language: string;
  items: Transcription[];
};

function groupByLanguage(transcriptions: Transcription[]): LanguageGroup[] {
  return transcriptions.reduce((acc: LanguageGroup[], item) => {
    let group = acc.find((g) => g.language === item.language);
    if (!group) {
      group = { language: item.language, items: [] };
      acc.push(group);
    }
    group.items.push(item);
    return acc;
  }, []);
}

const allTranscriptionsQuery = (contentId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ transcription: store.collection("transcription") })
    .where(({ transcription }) => eq(transcription.contentId, contentId));

export default function TranscriptionsTable({ item }: { item: Item }) {
  const [activeTab, setActiveTab] = useState(0);

  const allTranscriptions = useLiveQuery((q) => allTranscriptionsQuery(item.contentId)(q)).data ?? [];

  const sorted = [...allTranscriptions].sort((a, b) => {
    if (a.transcriptionType !== b.transcriptionType) return a.transcriptionType.localeCompare(b.transcriptionType);
    if (a.format !== b.format) return a.format.localeCompare(b.format);
    if (a.language !== b.language) return a.language.localeCompare(b.language);
    return (a.priority ?? 0) - (b.priority ?? 0);
  });

  const groups = groupByLanguage(sorted);

  if (groups.length === 0) {
    return <Typography color="text.secondary">No transcriptions</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} variant="scrollable" scrollButtons="auto">
        {groups.map((group, idx) => (
          <Tab key={group.language} label={`${group.language} (${group.items.length})`} value={idx} />
        ))}
      </Tabs>

      {groups.map((group, idx) => (
        <Box key={group.language} hidden={activeTab !== idx}>
          {activeTab === idx && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell sx={{ minWidth: 300 }}>Content</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.items.map((row) => (
                    <TableRow key={row.id} sx={{ "&:last-child td": { border: 0 } }}>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>{row.transcriptionType}</span>
                          {row.translatedFrom && (
                            <Chip
                              label={`from ${row.translatedFrom}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.65rem" }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.format} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>{row.backendModel || "-"}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            maxWidth: 400,
                            maxHeight: 100,
                            overflow: "auto",
                            fontSize: "0.8rem",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {row.format.match(/ssml/i) ? (
                            <code>{row.transcriptionContent}</code>
                          ) : (
                            row.transcriptionContent
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{row.confidence ? `${Math.round(Number(row.confidence) * 100)}%` : "-"}</TableCell>
                      <TableCell>
                        {row.url && (
                          <MuiLink href={row.url} target="_blank" rel="noreferrer" sx={{ fontSize: "0.75rem" }}>
                            JSON
                          </MuiLink>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ))}
    </Stack>
  );
}
